import { useState } from 'react';
import { Button } from '../../components/Button';
import { UserPicker } from '../../components/UserPicker';
import { setCoachRole, ApiError } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { fullName } from '../../lib/format';
import type { AdminLookupUser, ActiveSeriesExistDetail } from '../../lib/types';

interface Props {
  /** Called after a successful role toggle so parent can refetch series list. */
  onChanged: () => void;
}

export function ManageCoachRole({ onChanged }: Props) {
  const { push } = useToast();
  const [user, setUser] = useState<AdminLookupUser | null>(null);
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    if (!user) return;
    setToggling(true);
    try {
      const updated = await setCoachRole(user.id, !user.is_coach);
      setUser(updated);
      push(
        'success',
        updated.is_coach ? `${fullName(updated)} is now a coach.` : `${fullName(updated)} is no longer a coach.`,
      );
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const d = err.detail as ActiveSeriesExistDetail | undefined;
        const count = d?.active_series_count ?? 0;
        push(
          'error',
          `Cannot demote — ${count} active series. End them first on this page.`,
        );
      } else {
        push('error', err instanceof Error ? err.message : 'Toggle failed');
      }
    } finally {
      setToggling(false);
    }
  };

  return (
    <section
      style={{
        background: PS.white,
        border: `1px solid ${PS.borderLight}`,
        borderRadius: 4,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <header style={{ ...T.subheading, color: PS.nearBlack, margin: 0 }}>
        Manage coach role
      </header>
      <p style={{ ...T.caption, margin: 0 }}>
        Look up a user by name, phone, or email, then promote or demote.
      </p>

      <UserPicker
        selected={user}
        onSelect={setUser}
        placeholder="Search by name, phone, or email…"
      />

      {user && (
        <div
          style={{
            background: PS.pageSecondary,
            border: `1px solid ${PS.borderLight}`,
            borderRadius: 3,
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ ...T.bodyBold }}>{fullName(user)}</div>
              <div style={{ ...T.caption, color: PS.textSec }}>
                {user.phone ?? '—'}{user.email ? ` · ${user.email}` : ''}
              </div>
              <div style={{ ...T.caption, color: PS.textSec, marginTop: 4 }}>
                Coach: <strong>{user.is_coach ? 'yes' : 'no'}</strong> · Admin:{' '}
                <strong>{user.is_admin ? 'yes' : 'no'}</strong> · Credit:{' '}
                <strong>{user.credit_minutes} min</strong>
              </div>
            </div>
            <Button
              variant={user.is_coach ? 'danger' : 'primary'}
              onClick={() => void handleToggle()}
              disabled={toggling}
            >
              {toggling
                ? 'Working…'
                : user.is_coach
                  ? 'Demote (remove coach role)'
                  : 'Promote to coach'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
